/// <amd-module name="Wit/QueriesHub/Common/Components/AddToDashboardPanel" />
import { Dashboard, DashboardScope, Widget, WidgetSize } from "Dashboards/Common/Contracts/Dashboard";
import { IDashboardRestClient } from "Dashboards/Common/RestClient/Dashboard";
import { DashboardOwner, getMyTeams } from "Dashboards/DashboardPickerProvider/DashboardPickerProviderFactory";
import * as React from "react";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { VssComponent } from "VSS/Platform/Layout";
import { publishEvent } from "VSS/Platform/Telemetry";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxGroup, IListBoxItem } from "VSSUI/ListBox";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import * as Resources from "Wit/QueriesHub/Resources";

export interface WidgetData {
    /** Size of the widget tile */
    size: WidgetSize;

    /** Name of the widget */
    name: string;

    /** Widget type, expressed as contribution id */
    contributionId: string;

    /** Widget settings */
    settings: any;
}

export interface IAddToDashboardPanelProps {
    onDismiss?: () => void;

    // Current context project.
    projectId: string;

    // Information required to create a pinned widget.
    widgetOptions: WidgetData;

    // Parameters required for publishing telemetry
    telemetryProps: {
        parentPageName: string
    }
}

const allTeamDashboardsGroup: IListBoxGroup = {
    id: "add-to-dashboard-all-team-dashboards-group",
    name: Resources.AddToDashboardPanel_AllTeamDashboardsGroupHeader
};
const allProjectDashboardsGroup: IListBoxGroup = {
    id: "add-to-dashboard-all-project-dashboards-group",
    name: Resources.AddToDashboardPanel_AllProjectDashboardsGroupHeader
};
const favoritesGroup: IListBoxGroup = {
    id: "add-to-dashboard-favorite-group",
    name: Resources.AddToDashboardPanel_FavoritesGroupHeader
};

export class AddToDashboardPanel extends VssComponent<IAddToDashboardPanelProps> {
    private _dashboardItemProvider: GroupedItemProvider = new GroupedItemProvider([], [], true);
    private _areDashboardsLoading = new ObservableValue(true);
    private _errorMessage = new ObservableValue("");
    private _selectedDashboard: ObservableValue<Dashboard | undefined> = new ObservableValue(undefined);

    public async componentDidMount() {
        const data = await this.fetchData();
        this.processData(data.allDashboards, data.favoriteDashboards, data.allOwners, data.myTeams);
    }

    public render() {
        return (
            <Observer selectedDashboard={this._selectedDashboard}>
                {(observableProps: { selectedDashboard: Dashboard | undefined }) => {
                    return (
                        <Panel
                            onDismiss={this._onDismiss}
                            titleProps={{ text: Resources.AddToDashboardPanel_Header }}
                            lightDismiss={true}
                            footerButtonProps={[
                                {
                                    ariaLabel: Resources.Cancel,
                                    onClick: this._onDismiss,
                                    text: Resources.Cancel
                                },
                                {
                                    ariaLabel: Resources.OK,
                                    className: "margin-right-8",
                                    disabled: !observableProps.selectedDashboard,
                                    onClick: this._onSaveAndClose,
                                    primary: true,
                                    text: Resources.OK
                                }
                            ]}
                        >
                            <Observer errorMessage={this._errorMessage} isLoading={this._areDashboardsLoading}>
                                {(observableProps: { errorMessage: string; isLoading: boolean }) => {
                                    return (
                                        <div className="flex-column">
                                            <span className={"margin-top-8 margin-bottom-16"}>
                                                {Resources.AddToDashboardPanel_SubHeaderPreText}
                                                <b>{this.props.widgetOptions && this.props.widgetOptions.name}</b>
                                                {Resources.AddToDashboardPanel_SubHeaderPostText}
                                            </span>
                                            {observableProps.isLoading
                                                ? this.renderSpinner()
                                                : observableProps.errorMessage
                                                ? this.renderErrorMessage()
                                                : this.renderDashboardPicker()}
                                        </div>
                                    );
                                }}
                            </Observer>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private renderSpinner(): JSX.Element {
        return <Spinner className={"margin-16"} size={SpinnerSize.large} label={Resources.AddToDashboard_LoadingText} />;
    }

    private renderErrorMessage(): JSX.Element {
        return <MessageBar severity={MessageBarSeverity.Error}>{Resources.AddToDashboard_GetDashboardsFailedError}</MessageBar>;
    }

    private renderDashboardPicker(): JSX.Element {
        return (
            <Dropdown
                items={this._dashboardItemProvider}
                placeholder={Resources.AddToDashboardPanel_Header}
                onSelect={(e: React.SyntheticEvent, item: IListBoxItem<Dashboard>) => {
                    e.preventDefault();
                    this._onSelect(item);
                }}
                showFilterBox={true}
            />
        );
    }

    private _onSaveAndClose = async (): Promise<void> => {
        if (this._selectedDashboard.value) {
            const widget = {
                size: this.props.widgetOptions.size,
                id: "",
                position: {
                    column: 0,
                    row: 0
                },
                name: this.props.widgetOptions.name,
                contributionId: this.props.widgetOptions.contributionId,
                settings: this.props.widgetOptions.settings ?? "",
                artifactId: ""
            } as Widget;
            const teamContext: TeamContext = {
                projectId: this.props.projectId,
                project: "",
                teamId: this._selectedDashboard.value?.groupId || "", // team information is empty for project dashboards.
                team: this._selectedDashboard.value?.groupId || ""
            };
            const dashboardClient = this.context.pageContext.getRestClient<IDashboardRestClient>("IDashboardRestClient");
            await dashboardClient.createWidget(widget, teamContext, this._selectedDashboard.value?.id);
            if (this.props.onDismiss) {
                this.props.onDismiss();
            }

            // Publish telemetry when user successfully copies widget to dashbard
            publishEvent(this.context.pageContext, "Dashboards", "CopyWidgetToDashboard", {
                source: this.props.telemetryProps.parentPageName,
                widgetContributionId: widget.contributionId
            })
        }
    };

    private _onSelect(item: IListBoxItem<Dashboard>) {
        this._selectedDashboard.value = item.data;
    }

    private _onDismiss = (): void => {
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };

    private async fetchData(): Promise<{
        allDashboards: Dashboard[];
        favoriteDashboards: Dashboard[];
        allOwners: DashboardOwner[];
        myTeams: DashboardOwner[];
    }> {
        try {
            const contributionService: IVssContributionService = this.context.pageContext.getService("IVssContributionService");

            const allDashboardsResult = await contributionService.getDataAsync<{ dashboards: Dashboard[]; owners: DashboardOwner[] }>(
                "ms.vss-dashboards-web.all-dashboards-data-provider"
            );
            const favoriteDashboardsResult = await contributionService.getDataAsync<{ dashboard: Dashboard; owner: DashboardOwner }[]>(
                "ms.vss-dashboards-web.favorite-dashboards-data-provider"
            );
            const myTeams = (await getMyTeams(this.context.pageContext)) || [];

            const allDashboards = (allDashboardsResult && allDashboardsResult.dashboards) || [];
            const allOwners = (allDashboardsResult && allDashboardsResult.owners) || [];

            const favoriteDashboards = (favoriteDashboardsResult && favoriteDashboardsResult.map(item => item.dashboard)) || [];

            return { allDashboards, favoriteDashboards, allOwners: allOwners, myTeams };
        } catch (error) {
            this._errorMessage.value = error.message;
            return { allDashboards: [], favoriteDashboards: [], allOwners: [], myTeams: [] };
        }
    }

    private processData(allDashboards: Dashboard[], favoriteDashboards: Dashboard[], allOwners: DashboardOwner[], myTeams: DashboardOwner[]): void {
        const allOwnersNameMap: { [key: string]: string } = {};
        for (const owner of allOwners) {
            allOwnersNameMap[owner.id] = owner.name;
        }

        const dashboardPickerItems: IListBoxItem<Dashboard>[] = [];

        // Copy all team dashboards with their team as a group key to show them in the team groups.
        // Dashboards with teams not matching any of the teams in `myTeams` aren't shown.
        const teamDashboards = allDashboards.filter(d => allOwnersNameMap[d.ownerId] && d.dashboardScope === DashboardScope.Project_Team);
        dashboardPickerItems.push(
            ...teamDashboards.map(
                (d): IListBoxItem<Dashboard> => {
                    return { id: d.id, data: d, groupId: d.ownerId, text: d.name };
                }
            )
        );

        // Copy all project and team dashboards under their respective categories.
        dashboardPickerItems.push(
            ...allDashboards.map(
                (d): IListBoxItem<Dashboard> => {
                    const ownerName = allOwnersNameMap[d.ownerId];
                    const groupKey = d.dashboardScope === DashboardScope.Project ? allProjectDashboardsGroup.id : allTeamDashboardsGroup.id;
                    const displayName: string = ownerName ? `${ownerName} - ${d.name}` : `${d.name}`;

                    return {
                        id: d.id,
                        data: d,
                        groupId: groupKey,
                        text: displayName
                    };
                }
            )
        );

        // Copy favorite dashboards with the "favorite" group key.
        dashboardPickerItems.push(
            ...favoriteDashboards.map(
                (d): IListBoxItem<Dashboard> => {
                    const ownerName = allOwnersNameMap[d.ownerId];
                    const displayName: string = ownerName ? `${ownerName} - ${d.name}` : `${d.name}`;
                    return {
                        id: d.id,
                        data: d,
                        groupId: favoritesGroup.id,
                        text: displayName
                    };
                }
            )
        );

        // Sort the items to show them in each group in alphabetical order.
        dashboardPickerItems.sort((a, b) => localeIgnoreCaseComparer(a.text ?? "", b.text ?? ""));

        // Create groups for the teams, sort them to display in alphabetical order,
        // and sandwich them between the favorites and all groups.
        const groups: IListBoxGroup[] = [
            favoritesGroup,
            ...myTeams
                .map(item => {
                    return { id: item.id, name: item.name };
                })
                .sort((a, b) => localeIgnoreCaseComparer(a.name, b.name))
        ];

        groups.push(allTeamDashboardsGroup);
        groups.push(allProjectDashboardsGroup);

        this._dashboardItemProvider.pushGroups(...groups);
        this._dashboardItemProvider.push(...dashboardPickerItems);
        this._areDashboardsLoading.value = false;
    }
}
